import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { loadCurrentState, saveCurrentState } from './storage';
import { CPRState as StorageCPRState } from './types';

export interface CPRState {
  isRunning: boolean;
  startTime: number;              // timestamp in milliseconds when CPR started
  lastAdrenalineTime: number;     // timestamp in milliseconds when last adrenaline was given
  lastMassagerTime: number;       // timestamp in milliseconds when last massager change
  adrenalineCount: number;
  shockCount: number;
  status: 'ACTIVE' | 'DEATH' | 'ROSC' | null;
  endTime: number | null;
}

type CPRAction =
  | { type: 'START_CPR' }
  | { type: 'END_CPR'; status: 'DEATH' | 'ROSC'; time: number }
  | { type: 'TICK' }
  | { type: 'INCREMENT_ADRENALINE' }
  | { type: 'INCREMENT_SHOCK' }
  | { type: 'SET_LAST_MASSAGER_TIME' }  // New action for updating massager time
  | { type: 'LOAD_STATE'; state: Partial<CPRState> };

const initialState: CPRState = {
  isRunning: false,
  startTime: 0,
  lastAdrenalineTime: 0,
  lastMassagerTime: 0,
  adrenalineCount: 0,
  shockCount: 0,
  status: null,
  endTime: null
};

const convertStorageToContextState = (storageState: StorageCPRState | null): Partial<CPRState> => {
  if (!storageState) return {};
  
  return {
    isRunning: storageState.endState?.status === 'ACTIVE',
    startTime: storageState.timers?.startTime ?? Date.now(),
    lastAdrenalineTime: storageState.timers?.lastAdrenalineTime ?? 0,
    lastMassagerTime: storageState.timers?.lastMassagerTime ?? Date.now(),
    adrenalineCount: storageState.counters?.adrenalineCount ?? 0,
    shockCount: storageState.counters?.shockCount ?? 0,
    status: storageState.endState?.status ?? null,
    endTime: storageState.endState?.endTime ?? null,
  };
};

const convertContextToStorageState = (state: CPRState): Partial<StorageCPRState> => {
  return {
    timers: {
      startTime: state.startTime,
      lastAdrenalineTime: state.lastAdrenalineTime,
      lastMassagerTime: state.lastMassagerTime
    },
    counters: {
      adrenalineCount: state.adrenalineCount,
      shockCount: state.shockCount
    },
    endState: {
      status: state.status,
      endTime: state.endTime
    }
  };
};

function cprReducer(state: CPRState, action: CPRAction): CPRState {
  switch (action.type) {
    case 'START_CPR':
      const now = Date.now();
      return { 
        ...state, 
        isRunning: true, 
        status: 'ACTIVE',
        startTime: now,
        endTime: null,
        lastMassagerTime: now
      };
    case 'END_CPR':
      return {
        ...state,
        isRunning: false,
        status: action.status,
        endTime: action.time
      };
    case 'TICK':
      if (!state.isRunning) return state;
      return state; // No need to update any counters anymore
    case 'INCREMENT_ADRENALINE':
      return { 
        ...state, 
        adrenalineCount: state.adrenalineCount + 1,
        lastAdrenalineTime: Date.now()
      };
    case 'INCREMENT_SHOCK':
      return { ...state, shockCount: state.shockCount + 1 };
    case 'SET_LAST_MASSAGER_TIME':
      return { ...state, lastMassagerTime: Date.now() };
    case 'LOAD_STATE':
      return { ...state, ...action.state };
    default:
      return state;
  }
}

const CPRStateContext = createContext<{
  state: CPRState;
  dispatch: React.Dispatch<CPRAction>;
} | null>(null);

export const CPRStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cprReducer, initialState);

  useEffect(() => {
    const savedState = loadCurrentState();
    const convertedState = convertStorageToContextState(savedState);
    if (Object.keys(convertedState).length > 0) {
      dispatch({ type: 'LOAD_STATE', state: convertedState });
    }
  }, []);

  useEffect(() => {
    if (!state.isRunning && !state.status) return;
    const storageState = convertContextToStorageState(state);
    saveCurrentState(storageState);
  }, [state]);

  return (
    <CPRStateContext.Provider value={{ state, dispatch }}>
      {children}
    </CPRStateContext.Provider>
  );
};

export const useCPRState = () => {
  const context = useContext(CPRStateContext);
  if (!context) {
    throw new Error('useCPRState must be used within a CPRStateProvider');
  }
  return context;
};

export default CPRStateProvider;